import axios from 'axios';
import Cookies from 'js-cookie';
import { currentApiConfig } from '../constants/env';
import { sanitizeErrorMessage } from '../utils/errorHandler';

// Create axios instance with base configuration
const apiClient = axios.create({
	baseURL: currentApiConfig.baseURL,
	timeout: currentApiConfig.timeout,
	withCredentials: true, // Enable cookies
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json',
		'X-Requested-With': 'XMLHttpRequest' // CSRF protection
	}
});

// Request interceptor for adding auth tokens, logging, etc.
apiClient.interceptors.request.use(
	(config) => {
		// JWT Authentication is now handled via httpOnly cookies automatically
		// No need to manually add Authorization header

		// CSRF token handling
		const csrfToken = Cookies.get('XSRF-TOKEN');		if (csrfToken) {
			config.headers['X-XSRF-TOKEN'] = csrfToken;
		}

		// Log requests in development
		if (import.meta.env.DEV) {
			// API request logging removed for production
		}

		return config;
	}, (error) => {
		// Handle request errors silently in production
		return Promise.reject(error);
	}
);

// Refresh-token coordination: a 401 means the short-lived access token expired.
// We attempt one silent POST /auth/refresh, queueing any other requests that 401
// concurrently so only a single refresh call goes out, then retry them all.
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (success) => {
	refreshSubscribers.forEach((cb) => cb(success));
	refreshSubscribers = [];
};

const redirectToLogin = () => {
	Cookies.remove('auth-status');
	const publicAuthPaths = ['/login', '/reset-password', '/forgot-password'];
	if (!publicAuthPaths.includes(window.location.pathname)) {
		window.location.href = '/login';
	}
};

// A 409 means another tab won a concurrent refresh-token rotation race. Refresh-token
// cookies are shared across tabs in the same browser, so the winner's Set-Cookie has
// already updated the cookie this tab will send — wait briefly and retry once before
// giving up. Any other failure (401, network error) is not retried.
export const refreshWithRaceRetry = async () => {
	try {
		await apiClient.post('/api/v1.0/auth/refresh');
	} catch (err) {
		if (err.response?.status !== 409) {
			throw err;
		}
		await new Promise((resolve) => setTimeout(resolve, 250));
		await apiClient.post('/api/v1.0/auth/refresh');
	}
};

// Response interceptor for error handling and logging
apiClient.interceptors.response.use(
	(response) => {		// Log successful responses in development
		if (import.meta.env.DEV) {
			// API response logging removed for production
		}
		return response;
	},	async (error) => {
		const { config, response } = error;

		// Handle common error scenarios
		if (response?.status === 401) {
			const publicAuthPaths = ['/login', '/reset-password', '/forgot-password'];
			const isAuthEndpoint = config?.url?.includes('/auth/refresh') || config?.url?.includes('/auth/login');

			if (publicAuthPaths.includes(window.location.pathname) || isAuthEndpoint || config?._retry) {
				// Already on a public auth page, the failure IS the refresh/login call itself,
				// or we already retried once — don't loop, just clear state and bail.
				redirectToLogin();
				return Promise.reject(error);
			}

			config._retry = true;

			if (isRefreshing) {
				// A refresh is already in flight — queue this request and retry once it settles.
				return new Promise((resolve, reject) => {
					refreshSubscribers.push((success) => {
						if (success) {
							resolve(apiClient(config));
						} else {
							reject(error);
						}
					});
				});
			}

			isRefreshing = true;
			try {
				await refreshWithRaceRetry();
				isRefreshing = false;
				onRefreshed(true);
				return apiClient(config);
			} catch (refreshError) {
				isRefreshing = false;
				onRefreshed(false);

				// A network error or 5xx from /auth/refresh means the server is unreachable
				// (e.g. mid-deploy restart) — the refresh token itself was never rejected, so
				// don't force a logout. Only a definitive response (401 invalid token, or a
				// repeated 409 race failure) means the session is actually done.
				const refreshStatus = refreshError.response?.status;
				const isTransientFailure = !refreshError.response || refreshStatus >= 500;
				if (!isTransientFailure) {
					redirectToLogin();
				}
				return Promise.reject(error);
			}
		} else if (error.response?.status === 403) {
			// Forbidden - handle silently
		} else if (error.response?.status >= 500) {
			// Server errors - handle silently
		}

		// Sanitize error messages
		if (error.response?.data?.message) {
			error.response.data.message = sanitizeErrorMessage(error.response.data.message);
		}

		// Handle general API errors silently in production
		return Promise.reject(error);
	}
);

// Retry mechanism for failed requests
const retryRequest = async (fn, retries = currentApiConfig.retries) => {
	try {
		return await fn();
	} catch (error) {
		if (retries > 0 && error.response?.status >= 500) {
			// Retry silently without logging
			await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
			return retryRequest(fn, retries - 1);
		}
		throw error;
	}
};

// Generic API methods
export const api = {
	// GET request
	get: async (endpoint, config = {}) => {
		return retryRequest(() => apiClient.get(endpoint, config));
	},

	// POST request
	post: async (endpoint, data, config = {}) => {
		return retryRequest(() => apiClient.post(endpoint, data, config));
	},

	// PUT request
	put: async (endpoint, data, config = {}) => {
		return retryRequest(() => apiClient.put(endpoint, data, config));
	},

	// DELETE request
	delete: async (endpoint, config = {}) => {
		return retryRequest(() => apiClient.delete(endpoint, config));
	},

	// PATCH request
	patch: async (endpoint, data, config = {}) => {
		return retryRequest(() => apiClient.patch(endpoint, data, config));
	}
};

export default api;
